import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Tab, Tabs } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';

import { FBMAccountPicker, TabPanel } from 'shared';

import { ViewAllFBMIngestResults } from './FBMIngestResults/ViewAllFBMIngestResults';
import { ViewOrderDeepDive } from './orders/viewOrderDeepDive';
import { ViewAllOrdersV2 } from './ordersV2/viewAllOrdersV2';

function FBMViewOrders() {
    const location = useLocation();
    const history = useHistory();

    const getQueryParams = () => {
        const params = new URLSearchParams(location.search);
        return {
            tab: params.get('tab') || undefined,
            fbmOrderNum: params.get('fbmOrderNum') ? decodeURIComponent(params.get('fbmOrderNum')) : undefined,
            q: params.get('q') ? decodeURIComponent(params.get('q')) : undefined,
            types: params.get('types')
                ? params
                      .get('types')
                      .split(',')
                      .map((t) => decodeURIComponent(t.trim()))
                      .filter(Boolean)
                : [],
        };
    };

    const [queryParams, setQueryParams] = useState(getQueryParams());

    const [selectedTab, setSelectedTab] = useState(queryParams.tab || '0');

    const handleChange = (event, value) => {
        setSelectedTab(value);
        setQueryParams({ tab: value });
    };

    // When the url changes update the query params state variable.
    useEffect(() => {
        setQueryParams(getQueryParams());
    }, [location.search]);

    // Controls what to do when the query params change i.e. change the tab we are on.
    useEffect(() => {
        setSelectedTab(queryParams.tab || '0');
    }, [queryParams]);

    const handleFbmOrderNumChange = useCallback((value) => {
        setQueryParams((prev) => ({ ...prev, fbmOrderNum: value }));
    }, []);

    const handleQChange = useCallback((value) => {
        setQueryParams((prev) => ({ ...prev, q: value }));
    }, []);

    const handleTypesChange = useCallback((types) => {
        setQueryParams((prev) => ({ ...prev, types }));
    }, []);

    // Controls whats in the url
    useEffect(() => {
        const updatedParams = { tab: selectedTab, ...queryParams };
        const filteredParams = Object.fromEntries(
            Object.entries(updatedParams)
                .filter(([key, value]) => {
                    if (value === undefined || value === '') return false;
                    if (key === 'types' && Array.isArray(value) && value.length === 0) return false;
                    return true;
                })
                .map(([key, value]) => {
                    if (key === 'fbmOrderNum' || key === 'q') {
                        return [key, encodeURIComponent(value)];
                    }
                    if (key === 'types' && Array.isArray(value)) {
                        return [key, value.map((t) => encodeURIComponent(t)).join(',')];
                    }
                    return [key, value];
                }),
        );

        const searchParams = new URLSearchParams(filteredParams);
        history.replace(`?${searchParams.toString()}`);
    }, [selectedTab, history, queryParams]);

    return (
        <Paper>
            <FBMAccountPicker filterNonFBMAccounts />
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab id="viewAllOrders_Tab" label="Orders" value="0" />
                <Tab id="viewOrderDeepDive_Tab" label="Order Deep Dive" value="1" />
                <Tab id="viewAllFBMIngestResutls_Tab" label="FBM Ingest Results" value="2" />
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <ViewAllOrdersV2 />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <ViewOrderDeepDive
                    fbmOrderNum={queryParams.fbmOrderNum}
                    onFbmOrderNumChange={handleFbmOrderNumChange}
                />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="2">
                <ViewAllFBMIngestResults
                    onQChange={handleQChange}
                    q={queryParams.q}
                    typesParam={queryParams.types}
                    onTypesChange={handleTypesChange}
                />
            </TabPanel>
        </Paper>
    );
}

export { FBMViewOrders };
